import SuccessCheckmark from "./SuccessCheckmark";
import useCopyToClipboard from "../hooks/useCopyToClipboard";
import { ENQUIRY_TEAM, ENQUIRY_RESPONSE_TIME, type EnquiryType } from "../data/enquiryTypes";

interface EnquiryConfirmationProps {
  type: EnquiryType;
  referenceNumber: string;
  onSendAnother?: () => void;
}

/**
 * Shared across all 4 ZP-55 enquiry forms (ZP-60's own AC mandates identical behavior
 * across them). The reference number is real: server-issued by `POST /api/public/enquiries/*`
 * (`backend/app/services/enquiries.py::EnquiryService.submit`, collision-checked against
 * Firestore, not the old client-only `enquiryReference.ts` generator — see
 * `utils/{contact,supplier,franchise,b2b}Enquiry.ts`). What's NOT claimed: that a
 * confirmation email has been sent — that requires an email-sending integration that
 * doesn't exist yet — so it's stated honestly as pending instead.
 */
export default function EnquiryConfirmation({ type, referenceNumber, onSendAnother }: EnquiryConfirmationProps) {
  const { copy, copied } = useCopyToClipboard();

  return (
    <div role="status" aria-live="polite" className="flex flex-col items-center justify-center py-8 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-teal-400/15 text-teal-500">
        <SuccessCheckmark className="h-6 w-6" />
      </div>
      <h3 className="mt-4 font-display text-xl font-semibold text-ocean-950 dark:text-foam-50">Enquiry received</h3>
      <p className="mt-2 max-w-sm text-sm text-ocean-800/70 dark:text-foam-100/70">
        Your enquiry has been routed to our {ENQUIRY_TEAM[type]} team. Typical response time:{" "}
        {ENQUIRY_RESPONSE_TIME[type]}.
      </p>

      <button
        type="button"
        onClick={() => copy(referenceNumber)}
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-foam-100 px-4 py-2 font-mono text-sm font-semibold text-ocean-950 hover:bg-foam-200 dark:bg-white/10 dark:text-foam-50 dark:hover:bg-white/15"
      >
        {referenceNumber}
        <span className="text-xs font-sans font-normal text-ocean-800/50 dark:text-foam-100/50">
          {copied ? "Copied!" : "Tap to copy"}
        </span>
      </button>

      <p className="mt-4 max-w-sm text-xs text-ocean-800/50 dark:text-foam-100/50">
        Quote this reference if you follow up with us. A confirmation email isn't sent automatically yet —
        keep this number somewhere safe.
      </p>

      {onSendAnother && (
        <button
          type="button"
          onClick={onSendAnother}
          className="mt-6 text-sm font-semibold text-teal-600 hover:text-teal-700"
        >
          Send another enquiry
        </button>
      )}
    </div>
  );
}
